import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import DarkModeToggle from './ui/dark-mode-toggle';
import { useDarkMode } from './DarkModeContext';
import ProfilePicture from './ProfilePicture';

const TermsPage = ({ onClose, onSignIn, user, onShowPricing, onShowPrivacy, onShowCookiePreferences, onShowUpdates, mockupCount, onLogout }) => {
  const { isDarkMode } = useDarkMode();
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-surface-1/80 backdrop-blur-md flex-shrink-0 sticky top-0 z-50">
        <div className="w-full pl-6 pr-4 sm:pl-6 sm:pr-6 lg:pl-6 lg:pr-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center">
              <img 
                src={isDarkMode ? "/RandomMockups-Logo-LightMode.png" : "/RandomMockups-Logo.png"} 
                alt="RandomMockups Logo" 
                className="mr-1.5"
                style={{ height: '40px', width: 'auto' }}
              />
              <h1 
                className="text-xl font-semibold text-foreground cursor-pointer hover:text-muted-foreground transition-colors"
                onClick={onClose}
              >
                RandomMockups
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <DarkModeToggle />
              {!user && (
                <Button 
                  onClick={onSignIn}
                  variant="outline"
                  size="sm"
                  className="text-foreground hover:text-foreground bg-surface-2 hover:bg-muted"
                >
                  Sign in
                </Button>
              )}
              {user && (
                <button
                  onClick={() => {
                    onShowPricing();
                    window.history.pushState({}, '', '/pricing');
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-foreground hover:bg-muted transition-colors p-2 rounded-lg"
                >
                  <ProfilePicture user={user} className="w-6 h-6 rounded-full" />
                  <span className={`bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center ${
                    mockupCount >= 10 
                      ? 'rounded-full px-2 py-1 min-w-8 h-6' 
                      : 'rounded-full w-5 h-5'
                  }`}>
                    {mockupCount}
                  </span>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[900px] mx-auto px-6 py-8 flex-1">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Terms & Conditions
          </h1>
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">1. Service Description</h2>
            <p className="text-foreground mb-4">
              RandomMockups ("we," "our," or "us") provides an AI-powered platform that generates device mockups from uploaded images. Our service uses artificial intelligence to create realistic device mockups, including but not limited to mobile phones, tablets, and desktop computers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">2. User Accounts</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>2.1 Registration:</strong> To use our service, you must create an account by signing in with a supported authentication provider (e.g., Google). You are responsible for maintaining the confidentiality of your account credentials.
              </p>
              <p>
                <strong>2.2 Account Responsibilities:</strong> You are responsible for all activities that occur under your account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and complete information when creating your account</li>
                <li>Keep your account information updated</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Not share your account credentials with third parties</li>
              </ul>
              <p>
                <strong>2.3 Account Termination:</strong> We reserve the right to suspend or terminate your account at any time for violations of these Terms, fraudulent activity, or any other reason we deem necessary.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">3. Payment Terms</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>3.1 Pricing:</strong> Our service offers both free credits and paid plans. Pricing for paid plans is displayed on our pricing page and may be subject to change. We will notify you of any significant pricing changes.
              </p>
              <p>
                <strong>3.2 Payment Processing:</strong> Payments are processed through Stripe, a third-party payment processor. By making a purchase, you agree to Stripe's terms of service and privacy policy.
              </p>
              <p>
                <strong>3.3 Refunds:</strong> All sales are final. We do not offer refunds for purchased credits or subscription plans, except as required by law or at our sole discretion.
              </p>
              <p>
                <strong>3.4 Subscriptions:</strong> Monthly subscription plans will automatically renew unless cancelled. You may cancel your subscription at any time through your account settings or by contacting us. Cancellation will take effect at the end of your current billing period.
              </p>
              <p>
                <strong>3.5 Failed Payments:</strong> If a payment fails, we may suspend your account access until payment is successfully processed. We are not responsible for any loss of service or data due to failed payments.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">4. Intellectual Property</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>4.1 User Content:</strong> You retain all rights to images you upload to our service. By uploading content, you grant us a limited, non-exclusive license to use, process, and store your content solely for the purpose of providing the mockup generation service.
              </p>
              <p>
                <strong>4.2 Generated Mockups:</strong> You own the mockups generated from your uploaded content. However, you may not use our service to generate mockups that infringe on third-party intellectual property rights.
              </p>
              <p>
                <strong>4.3 Service Intellectual Property:</strong> All content, features, and functionality of our service, including but not limited to text, graphics, logos, and software, are owned by RandomMockups and protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                <strong>4.4 Prohibited Uses:</strong> You may not:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Reverse engineer, decompile, or disassemble our service</li>
                <li>Use our service to create content that violates any laws or regulations</li>
                <li>Resell or redistribute our service without authorization</li>
                <li>Use our service in a manner that could damage, disable, or impair our systems</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">5. Prohibited Uses</h2>
            <div className="text-foreground space-y-3">
              <p>You agree not to use our service to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Upload content that is illegal, harmful, threatening, abusive, or violates any laws</li>
                <li>Upload content that infringes on copyright, trademark, or other intellectual property rights</li>
                <li>Upload content that contains personal information of others without consent</li>
                <li>Upload malicious software, viruses, or other harmful code</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Use automated systems to access our service in a manner that exceeds reasonable usage</li>
                <li>Interfere with or disrupt the service or servers connected to the service</li>
              </ul>
              <p>
                Violation of this section may result in immediate termination of your account and may subject you to legal action.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">6. AI Disclaimer</h2>
            <div className="text-foreground space-y-3">
              <p>
                Our service uses artificial intelligence (specifically Google Gemini) to generate mockups. AI-generated content may:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Not always meet your expectations or requirements</li>
                <li>Contain inaccuracies or imperfections</li>
                <li>Vary in quality or style</li>
                <li>Be subject to the limitations and capabilities of the underlying AI technology</li>
              </ul>
              <p>
                We do not guarantee the accuracy, quality, or suitability of AI-generated mockups for any particular purpose. You use AI-generated content at your own risk.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">7. Limitation of Liability</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>7.1 Service Availability:</strong> We strive to provide reliable service, but we do not guarantee uninterrupted or error-free operation. Our service is provided "as is" and "as available" without warranties of any kind.
              </p>
              <p>
                <strong>7.2 Limitation of Damages:</strong> To the maximum extent permitted by law, RandomMockups shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities.
              </p>
              <p>
                <strong>7.3 Maximum Liability:</strong> Our total liability for any claims arising from or related to the service shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
              </p>
              <p>
                <strong>7.4 Third-Party Services:</strong> We use third-party services (Firebase, Stripe, Google Gemini) and are not responsible for their availability, performance, or data handling practices.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">8. Termination</h2>
            <div className="text-foreground space-y-3">
              <p>
                <strong>8.1 Termination by You:</strong> You may terminate your account at any time by contacting us or deleting your account through the service interface.
              </p>
              <p>
                <strong>8.2 Termination by Us:</strong> We may terminate or suspend your account immediately, without prior notice, if you violate these Terms or engage in fraudulent, illegal, or harmful activity.
              </p>
              <p>
                <strong>8.3 Effect of Termination:</strong> Upon termination, your right to use the service will cease immediately. We may delete your account data and uploaded content, though we reserve the right to retain certain information as required by law or for legitimate business purposes.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">9. Changes to Terms</h2>
            <p className="text-foreground mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of significant changes by updating the "Last updated" date at the top of this page. Your continued use of the service after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">10. Contact Information</h2>
            <p className="text-foreground mb-4">
              If you have questions about these Terms & Conditions, please contact us through the feedback form in the application or via email.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-surface-1 py-8">
        <div className="max-w-[1480px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground space-y-2 md:space-y-0">
            <div>
              All rights reserved
            </div>
            <div className="flex space-x-4">
              <button 
                onClick={() => {
                  onShowUpdates();
                  window.history.pushState({}, '', '/updates');
                }}
                className="hover:text-foreground transition-colors"
              >
                Updates
              </button>
              <button 
                onClick={() => {
                  onShowPricing();
                  window.history.pushState({}, '', '/pricing');
                }}
                className="hover:text-foreground transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => {
                  // Already on Terms page, no action needed
                }}
                className="hover:text-foreground transition-colors font-medium"
              >
                Terms
              </button>
              <button 
                onClick={() => {
                  onShowPrivacy();
                  window.history.pushState({}, '', '/privacy');
                }}
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </button>
              <button 
                onClick={onShowCookiePreferences}
                className="hover:text-foreground transition-colors"
              >
                Cookie Settings
              </button>
            </div>
            <div>
              Made with 🍌 by <a href="https://x.com/txh" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-foreground transition-colors">@txh</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;

